import { AxiosProgressEvent } from 'axios';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { LocalStore } from 'constants/localstore';
import { FileProgressData, FileQueueData } from 'services/sse.service';
import { downloadFile, uploadFileService } from 'services/file-flow.service';
import { GoogleMediaItem } from 'services/google/photos.service';
import { MAX_TOKEN_TIME } from 'services/google/google.params';
import { shareFile } from 'services/google/youtube.service';
import {
  anyProcessingFiles as anyProcessingFilesService,
  uploadFile,
  FileType,
  changeStatus,
} from 'services/file.service';
import { RootState } from 'store/store';
import { createSafeAsyncThunk } from 'store/store.helper';
import { isFilesProcessing, isFileStatusCompleted } from 'components/app/_helpers/_app.helper';
import { fetchFiles, putFile, removeFile, updateFileStatus } from './app-files.slice';
import {
  fetchStorageSize,
  fetchProjects,
  updateProjectStatusToUploading,
  updateProjectStatusToProcessing,
} from './app-data.slice';
import { INIT_THUMBNAIL, updateClipThumbnails } from 'components/app/_mediator/clip-thumb/clip-thumb.slice';
import { fetchClips } from 'components/app/editor/_mediator/editor.slice';

const SLICE_ID = 'app__filesUpload';

export type FileUplId = string; // `${projectId}__${fileName}`

export interface UploadingFile {
  id: FileUplId;
  projectId: ProjectId;
  fileName: string;
  progress: number;
  type: FileType;
}

interface UploadFiles {
  files: File[];
  reUpload: boolean;
}

interface UploadGFiles {
  files: GoogleMediaItem[];
  reUpload: boolean;
}

interface UploadYtFile {
  fcId: FinalcutId;
  metadata: string;
  url: Url;
}

interface DownloadFinalCut {
  fcId: FinalcutId;
  url: Url;
  fileName: string;
}

interface State {
  uploadingFiles: UploadingFile[];
  queueFiles: Record<FileId, number>;
  processingFiles: Record<FileId, number>;
  youtubeFiles: Record<FileId, number>;
  finalcutDownloads: Record<FileId, number>;
}

const initialState: State = {
  queueFiles: {},
  uploadingFiles: [],
  processingFiles: {},
  youtubeFiles: {},
  finalcutDownloads: {},
};

const uploadFileControllers: Record<FileUplId, AbortController> = {};

// T H U N K S

export const uploadFiles = createSafeAsyncThunk(
  SLICE_ID + '/uploadFiles',
  async (params: UploadFiles, { dispatch, getState }) => {
    const { selectedProject: projectId } = (getState() as RootState).projects;
    if (!projectId) return;
    const reUpload = params.reUpload;
    await Promise.all(
      params.files.map(async (file) => {
        const fileName = file.name;
        const { data } = await uploadFile({ projectId, key: fileName, reUpload: reUpload }, file.size);
        const mediaFile = data.file;
        const url = data.url;
        const type = getFileType(file);
        const id: FileUplId = `${projectId}__${fileName}`;
        uploadFileControllers[id] = new AbortController();
        const signal = uploadFileControllers[id].signal;
        const setProgress = (progress: number) => {
          dispatch(slice.actions.uploadingFileProgress({ id, projectId, fileName, progress, type }));
        };
        const onUploadProgress = (payload: AxiosProgressEvent) => {
          const progress = Math.floor((payload.loaded * 100) / payload.total!);
          setProgress(progress);
        };
        setProgress(0);
        dispatch(updateProjectStatusToUploading(projectId));
        uploadFileService({ url, file, onUploadProgress, signal, fileSize: file.size })
          .then(async () => {
            delete uploadFileControllers[id];
            const { data } = await changeStatus(mediaFile.id);
            if ((getState() as RootState).projects.selectedProject === projectId)
              await dispatch(putFile({ file: data, projectId }));
            dispatch(slice.actions.uploadingFileEnd(id));
          })
          .catch((err) => {
            if (err.code === 'ERR_CANCELED') dispatch(removeFile(mediaFile.id));
          });
      })
    );
  }
);

export const uploadGFiles = createSafeAsyncThunk(
  SLICE_ID + '/uploadGFiles',
  async (params: UploadGFiles, { dispatch, getState }) => {
    const projectId = (getState() as RootState).projects.selectedProject;
    const token = localStorage.getItem(LocalStore.googleOAuth);
    if (!projectId || !token) return;
    dispatch(updateProjectStatusToUploading(projectId));
    await Promise.all(
      params.files.map(async (file) => {
        const url = file.type === 'VIDEO' ? file.mediaFile.baseUrl + '=dv' : file.mediaFile.baseUrl + '=d';
        const key = file.mediaFile.filename;
        await uploadFile(
          {
            key,
            projectId,
            reUpload: params.reUpload,
            google: { url, token },
          },
          0
        );
      })
    );
    await dispatch(fetchFiles(projectId));
  }
);

export const uploadYtFile = createSafeAsyncThunk(
  SLICE_ID + '/uploadToYoutube',
  async ({ fcId, url, metadata }: UploadYtFile, { rejectWithValue, dispatch }) => {
    const token = localStorage.getItem(LocalStore.youtubeOAuth);
    const tokenDate = localStorage.getItem(LocalStore.youtubeOAuthDate);

    if (!token || !tokenDate) return rejectWithValue(401);

    if (Date.now() - Number(tokenDate) > MAX_TOKEN_TIME) {
      localStorage.removeItem(LocalStore.youtubeOAuth);
      localStorage.removeItem(LocalStore.youtubeOAuthDate);
      return rejectWithValue(403);
    }

    toast.info('Upload in progress. Feel free to browse Pulse; you’ll get a notification once it’s finished.');
    const fileName = decodeURI(new URL(url).pathname.split('/').pop()!);
    const setProgress = (progress: number) => dispatch(slice.actions.setYoutubeProgress({ fcId, progress }));
    const dwnCb = (e: number) => setProgress((e * 100) / 2);
    const uplCb = (e: number) => setProgress((e * 100) / 2 + 50);
    const response = await downloadFile(url, dwnCb);
    const file = new File([response.data], fileName);
    await shareFile(file, metadata, uplCb);
    dispatch(slice.actions.setYoutubeProgress({ fcId, progress: null }));
    toast.success('Video successfully uploaded to YouTube! 🎉');
  }
);

export const removeUploadingFile = createSafeAsyncThunk(
  SLICE_ID + '/removeUploadingFile',
  async (fileId: FileUplId, { dispatch }) => {
    if (!uploadFileControllers[fileId]) return;
    uploadFileControllers[fileId]?.abort();
    delete uploadFileControllers[fileId];
    dispatch(slice.actions.uploadingFileEnd(fileId));
  }
);

export const handleProcessingFiles = createSafeAsyncThunk(
  SLICE_ID + '/handleProcessingFiles',
  async (payload: FileProgressData, { getState, dispatch }) => {
    const { fileId, status, progress, projectId } = payload;
    const state = getState() as RootState;
    const currStatus = state.app__files.statuses[fileId];
    const isSelectedProject = !!currStatus;

    if (progress) {
      dispatch(slice.actions.processingFileProgress({ fileId, progress: Math.floor(progress) }));
    }
    if (isSelectedProject) {
      if (currStatus === status) return;
      dispatch(updateFileStatus({ fileId, status }));
    }
    if (isFileStatusCompleted(status)) {
      dispatch(fetchProjects());
      dispatch(fetchStorageSize());
      if (isSelectedProject) dispatch(fetchFiles(state.projects.selectedProject!));

      // Fetch highlights when a new video or image file becomes available
      const selectedStory = state.projects.selectedStory;
      const file = state.app__files.files[fileId];
      if (selectedStory && file && [FileType.video, FileType.image].includes(file.fileType)) {
        // Only fetches clips for this specific file from backend
        dispatch(fetchClips({ storyId: selectedStory, fileId }));
      }

      // Clear thumbnails for clips that reference this file so Highlights regenerates them lazily
      const selector = (getState() as RootState).editor.selector;
      const clipIds = selector?.[fileId];
      if (clipIds && clipIds.length) {
        const initMap = clipIds.reduce(
          (acc, cid) => {
            acc[cid] = INIT_THUMBNAIL;
            return acc;
          },
          {} as Record<ClipId, typeof INIT_THUMBNAIL>
        );
        dispatch(updateClipThumbnails(initMap));
      }

      if (
        (isSelectedProject ? !isFilesProcessing((getState() as RootState).app__files.statuses) : true) &&
        !(await dispatch(anyProcessingFiles()).unwrap())
      )
        return { closeSse: true };
    } else dispatch(updateProjectStatusToProcessing(projectId));
  }
);

export const anyProcessingFiles = createSafeAsyncThunk(SLICE_ID + '/anyProcessingFiles', async () => {
  const res = await anyProcessingFilesService();
  return res.data;
});

export const downloadFinalcut = createSafeAsyncThunk(
  SLICE_ID + '/downloadFinalCut',
  async ({ fcId, url, fileName }: DownloadFinalCut, { dispatch }) => {
    const response = await downloadFile(url, (val: number) =>
      dispatch(slice.actions.setFinalcutProgress({ fcId, progress: val * 100 }))
    );
    const safeFileName = fileName.replaceAll('.', '_');
    const file = new File([response.data], safeFileName, { type: response.data.type });
    const blobUrl = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = safeFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  }
);

/* H E L P E R S */

const getFileType = (file: File) => {
  return /^image/.test(file.type)
    ? FileType.image
    : /^video/.test(file.type)
    ? FileType.video
    : /^audio/.test(file.type)
    ? FileType.audio
    : FileType.unknown;
};

// S L I C E

export const slice = createSlice({
  name: SLICE_ID,
  initialState,
  reducers: {
    setFileQueue: (state, action: PayloadAction<FileQueueData[]>) => {
      const payload: State['queueFiles'] = {};
      action.payload.forEach((data) => {
        payload[data.fileId] = data.positionInQueue;
      });
      state.queueFiles = payload;
    },
    processingFileProgress: (state, action: PayloadAction<{ fileId: FileId; progress: number }>) => {
      const { fileId, progress } = action.payload;
      state.processingFiles[fileId] = progress;
    },
    setYoutubeProgress: (state, action: PayloadAction<{ fcId: FinalcutId; progress: number | null }>) => {
      const { fcId, progress } = action.payload;
      if (progress === null) delete state.youtubeFiles[fcId];
      else state.youtubeFiles[fcId] = Math.floor(progress);
    },
    uploadingFileProgress: (state, action: PayloadAction<UploadingFile>) => {
      const index = state.uploadingFiles.findIndex((el) => el.id === action.payload.id);
      if (index !== -1) state.uploadingFiles[index] = { ...action.payload };
      else state.uploadingFiles.push(action.payload);
    },
    uploadingFileEnd: (state, action: PayloadAction<string>) => {
      const newState = state.uploadingFiles.filter((el) => el.id !== action.payload);
      state.uploadingFiles = newState;
    },
    setFinalcutProgress: (state, action: PayloadAction<{ fcId: FinalcutId; progress: number }>) => {
      const { fcId, progress } = action.payload;
      if (progress === 100) delete state.finalcutDownloads[fcId];
      else state.finalcutDownloads[fcId] = Math.floor(progress);
    },
  },
});

export const { setFileQueue } = slice.actions;
export default slice.reducer;
